import { useState } from 'react'
import { Building2, Crown, MapPin, Network, Plus, ShieldAlert, Star, Users } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Checkbox, Field, TextInput } from '../admin/form.js'
import { Combobox } from '../admin/pickers.js'
import { useApi } from '../access/lib.js'
import { useConfirm } from '../shell/confirm.js'
import { Link, navigate } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { useEffect } from 'react'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'

/**
 * SIRH LOT 2 — ORGANISATION : Équipes (liste + détail), Compétences,
 * Organigramme. Tout est historisé côté serveur (clore + recréer) ; aucune
 * valeur d'équipe hardcodée — le référentiel est administrable.
 */

interface TeamRow { id: string; name: string; description: string | null; active: boolean; location: { id: string; name: string } | null; members: number; leads: string[] }
interface PeopleList { people: { id: string; display_name: string }[] }

/* ═══════════════ SIRH LOT 4.8 — AUTORITÉ ORGANISATIONNELLE (§8-19) ═══════════════

   Désigner un responsable d'équipe ou poser un rattachement hiérarchique n'est
   pas « modifier l'organisation » : ça ÉLARGIT le périmètre de quelqu'un sur
   des personnes. Ces actions-là :
     · exigent `hr.org.authority.manage` (+ vérification renforcée côté serveur),
       et sont ABSENTES — jamais grisées (DS §8) — quand le droit manque ;
     · annoncent leur impact AVANT d'être exécutées (`/authority/preview`) ;
     · quand elles élargissent le périmètre de l'acteur LUI-MÊME, le serveur
       répond 202 `pending` : une demande part en approbation chez un tiers.
   Le reste de l'organisation (nom, description, membre simple, compétences)
   continue de vivre avec `hr.org.edit`. */

export const AUTHORITY_PERM = 'hr.org.authority.manage'
export const AUTHORITY_HINT = 'Action d’autorité — exige le droit dédié et une vérification renforcée.'
export const AUTHORITY_PENDING_MESSAGE = 'Demande envoyée — un autre responsable habilité doit l’approuver.'
export const AUTHORITY_QUEUE_HREF = '/equipe/organisation/equipes'

export type AuthorityAction =
  | { kind: 'TEAM_LEAD_ADD'; team_id: string; person_id: string; effective_from: string }
  | { kind: 'TEAM_LEAD_END'; assignment_id: string; effective_until: string }
  | { kind: 'MANAGER_ADD'; person_id: string; manager_person_id: string; manager_kind: 'PRIMARY' | 'SECONDARY' | 'OPERATIONAL'; effective_from: string }
  | { kind: 'MANAGER_END'; assignment_id: string; effective_until: string }

export const AUTHORITY_ACTION_LABEL: Record<string, string> = {
  TEAM_LEAD_ADD: 'Désigner un responsable d’équipe',
  TEAM_LEAD_END: 'Sortir un responsable de son équipe',
  MANAGER_ADD: 'Poser un rattachement « Responsable »',
  MANAGER_END: 'Clore un rattachement « Responsable »',
}

export function authorityPreviewQuery(a: AuthorityAction): string {
  const p = new URLSearchParams({ kind: a.kind })
  if (a.kind === 'TEAM_LEAD_ADD') { p.set('team_id', a.team_id); p.set('person_id', a.person_id); p.set('effective_from', a.effective_from) }
  else if (a.kind === 'MANAGER_ADD') { p.set('person_id', a.person_id); p.set('manager_person_id', a.manager_person_id); p.set('manager_kind', a.manager_kind); p.set('effective_from', a.effective_from) }
  else { p.set('assignment_id', a.assignment_id); p.set('effective_until', a.effective_until) }
  return p.toString()
}

interface AuthorityPreview {
  subject_person_id: string | null
  before: number; after: number; delta: number
  teams_before: number | null; teams_after: number | null
  note: string | null
  /** Le serveur dit si l'action élargit le périmètre de l'ACTEUR (→ approbation). */
  requiresApproval: boolean
}

/** Garde de forme : un `{ error }`, un champ manquant ou une réponse
 *  inattendue donnent « aperçu indisponible », jamais un faux chiffre. */
export function shapeAuthorityPreview(v: unknown): AuthorityPreview | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o['error'] === 'string') return null
  if (typeof o['before'] !== 'number' || typeof o['after'] !== 'number') return null
  const delta = typeof o['delta'] === 'number' ? o['delta'] : o['after'] - o['before']
  return {
    subject_person_id: typeof o['subject_person_id'] === 'string' ? o['subject_person_id'] : null,
    before: o['before'], after: o['after'], delta,
    teams_before: typeof o['teams_before'] === 'number' ? o['teams_before'] : null,
    teams_after: typeof o['teams_after'] === 'number' ? o['teams_after'] : null,
    note: typeof o['note'] === 'string' ? o['note'] : null,
    requiresApproval: o['requires_approval'] === true,
  }
}

const gens = (n: number) => `${n} personne${n > 1 ? 's' : ''}`

/** « Périmètre de Léa — AVANT : 14 personnes · APRÈS : 27 personnes · +13 » (§18-19). */
export function impactSentence(name: string, p: AuthorityPreview): string {
  const signe = p.delta > 0 ? `+${p.delta}` : String(p.delta)
  return `Périmètre de ${name} — AVANT : ${gens(p.before)} · APRÈS : ${gens(p.after)} · ${signe}`
}

/**
 * APERÇU D'IMPACT + EXÉCUTION — surface COURTE (Modal ≤ 560 px, jamais un
 * drawer). Elle charge l'aperçu serveur à l'ouverture, exécute la mutation à
 * la confirmation (step-up rejoué par `adminMutate`), et sait rendre la
 * réponse 202 « demande envoyée » sans la faire passer pour une application.
 */
export function AuthorityImpactModal(props: {
  open: boolean
  action: AuthorityAction | null
  subjectName: string
  title: string
  /** Phrase de conséquence (DS §7 : le titre pose la question, le texte dit ce qui arrive). */
  consequence?: string | undefined
  confirmLabel: string
  request: { method: string; path: string; body: Record<string, unknown> } | null
  onClose: () => void
  onApplied: () => void
}) {
  const toast = useToast()
  const [preview, setPreview] = useState<AuthorityPreview | null | 'loading'>('loading')
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<string | null>(null)

  const q = props.action ? authorityPreviewQuery(props.action) : null
  useEffect(() => {
    if (!props.open || !q) return
    let vivant = true
    setPreview('loading'); setPending(null)
    apiGet<unknown>(`/api/v1/hr/org/authority/preview?${q}`)
      .then((r) => { if (vivant) setPreview(shapeAuthorityPreview(r)) })
      .catch(() => { if (vivant) setPreview(null) })
    return () => { vivant = false }
  }, [props.open, q])

  const confirmer = async () => {
    if (!props.request || busy) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string; pending?: boolean; approval_id?: string }>(
      props.request.method, props.request.path, props.request.body)
    setBusy(false)
    // 202 : rien n'a été appliqué — une demande attend un tiers (§14).
    if (r.status === 202 && r.data.pending === true) {
      setPending(r.data.message ?? '')
      return
    }
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Autorité mise à jour.')
    props.onApplied()
    props.onClose()
  }

  return (
    <Modal open={props.open} onClose={props.onClose} ariaLabel={props.title} width={560}>
      <div className="org-editor org-authority">
        <h3><ShieldAlert size={16} aria-hidden="true" /> {props.title}</h3>
        {pending !== null ? (
          <>
            <p className="org-authority__pending">{AUTHORITY_PENDING_MESSAGE}</p>
            {pending ? <p className="lc-hint" style={{ margin: 0 }}>{pending}</p> : null}
            <p className="lc-hint" style={{ margin: 0 }}>
              Rien n’a été appliqué. Vous ne pouvez pas approuver votre propre demande.
            </p>
            <div className="org-editor__foot">
              <Link href={AUTHORITY_QUEUE_HREF} className="lc-btn lc-btn--ghost">Voir la file des demandes</Link>
              <button className="lc-btn lc-btn--primary" onClick={props.onClose}>Fermer</button>
            </div>
          </>
        ) : (
          <>
            <p className="org-authority__hint">{AUTHORITY_HINT}</p>
            {preview === 'loading' ? <p className="lc-hint" style={{ margin: 0 }}>Calcul de l’impact…</p>
              : preview === null ? (
                <p className="lc-hint" style={{ margin: 0 }}>
                  Aperçu d’impact indisponible — le serveur n’a pas renvoyé le calcul.
                  L’action reste possible : c’est lui qui tranchera.
                </p>
              ) : (
                <>
                  <p className="org-authority__impact">{impactSentence(props.subjectName, preview)}</p>
                  {preview.teams_before !== null && preview.teams_after !== null ? (
                    <p className="lc-hint" style={{ margin: 0 }}>
                      Équipes concernées : {preview.teams_before} → {preview.teams_after}
                    </p>
                  ) : null}
                  {props.consequence ? <p className="lc-hint" style={{ margin: 0 }}>{props.consequence}</p> : null}
                  {preview.note ? <p className="org-authority__note">{preview.note}</p> : null}
                  {preview.requiresApproval ? (
                    <p className="org-authority__pending">
                      Cette action élargit VOTRE propre périmètre : elle ne sera pas appliquée
                      directement — elle partira en demande, et un autre responsable habilité devra l’approuver.
                    </p>
                  ) : null}
                </>
              )}
            <div className="org-editor__foot">
              <button className="lc-btn lc-btn--ghost" onClick={props.onClose}>Annuler</button>
              {/* Le bouton dit ce qui va VRAIMENT se passer : une action qui
                  élargit son propre périmètre part en demande, pas en application. */}
              <button className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void confirmer()}>
                {busy ? 'Envoi…' : preview !== null && preview !== 'loading' && preview.requiresApproval ? 'Envoyer la demande' : props.confirmLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

/* ─────────── FILE DES DEMANDES D'ÉLÉVATION (§13-15) ─────────── */

interface AuthorityRequestRow {
  id: string
  actionKind: string | null
  subject: string | null
  actorName: string | null
  proposedBy: string | null
  proposedAt: string | null
  before: number | null
  after: number | null
  delta: number | null
  note: string | null
  mine: boolean
}

const nombreOuNull = (v: unknown): number | null => (typeof v === 'number' ? v : null)
const memeNom = (a: string | null, b: string | null): boolean =>
  a !== null && b !== null && a.trim().toLocaleLowerCase('fr') === b.trim().toLocaleLowerCase('fr')

/** Le compte connecté, tel qu'il sert à reconnaître SES demandes. */
export interface MoiMinimal { id: string | null; displayName: string | null }

export function shapeAuthorityRequests(v: unknown, moi: MoiMinimal): AuthorityRequestRow[] | null {
  const brut = v && typeof v === 'object' ? (v as Record<string, unknown>)['requests'] ?? v : null
  if (!Array.isArray(brut)) return null
  return brut.flatMap((r) => {
    if (!r || typeof r !== 'object') return []
    const o = r as Record<string, unknown>
    if (typeof o['id'] !== 'string') return []
    const payload = (o['payload'] && typeof o['payload'] === 'object' ? o['payload'] : {}) as Record<string, unknown>
    const apercu = (payload['preview'] && typeof payload['preview'] === 'object' ? payload['preview'] : {}) as Record<string, unknown>
    const action = (payload['action'] && typeof payload['action'] === 'object' ? payload['action'] : payload) as Record<string, unknown>
    const proposedBy = typeof o['proposed_by'] === 'string' ? o['proposed_by']
      : typeof payload['proposed_by'] === 'string' ? payload['proposed_by'] : null
    const actorName = typeof o['actor_name'] === 'string' ? o['actor_name'] : null
    // « Est-ce MA demande ? » — l'identifiant quand le serveur le donne ; à
    // défaut le nom affiché. Le serveur refuse de toute façon l'auto-décision
    // (§14) : ici on évite seulement de PROPOSER une action vouée au refus.
    const mine = typeof o['is_mine'] === 'boolean'
      ? o['is_mine']
      : proposedBy !== null && moi.id !== null ? proposedBy === moi.id
      : memeNom(actorName, moi.displayName)
    return [{
      id: o['id'],
      actionKind: typeof action['kind'] === 'string' ? action['kind'] : null,
      subject: typeof o['subject'] === 'string' ? o['subject'] : null,
      actorName,
      proposedBy,
      proposedAt: typeof o['proposed_at'] === 'string' ? o['proposed_at'] : null,
      before: nombreOuNull(apercu['before']),
      after: nombreOuNull(apercu['after']),
      delta: nombreOuNull(o['preview_delta']) ?? nombreOuNull(apercu['delta']),
      note: typeof apercu['note'] === 'string' ? apercu['note'] : null,
      mine,
    }]
  })
}

/**
 * La file d'attente : ce que d'autres ont demandé et qui attend une décision.
 * Le bouton APPROUVER n'apparaît jamais sur sa propre demande — le serveur la
 * refuserait (§14), et l'UI ne propose pas une action vouée au refus.
 */
export function AuthorityRequestsPanel() {
  const toast = useToast()
  const [moi, setMoi] = useState<MoiMinimal>({ id: null, displayName: null })
  const [rows, setRows] = useState<AuthorityRequestRow[] | null | 'loading'>('loading')
  const [version, setVersion] = useState(0)
  const [decision, setDecision] = useState<{ row: AuthorityRequestRow; decision: 'APPROVED' | 'REJECTED'; note: string } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    apiGet<{ user?: { id?: unknown; display_name?: unknown } | null }>('/api/v1/me')
      .then((r) => setMoi({
        id: r && r.user && typeof r.user.id === 'string' ? r.user.id : null,
        displayName: r && r.user && typeof r.user.display_name === 'string' ? r.user.display_name : null,
      }))
      .catch(() => setMoi({ id: null, displayName: null }))
  }, [])
  useEffect(() => {
    let vivant = true
    setRows('loading')
    apiGet<unknown>('/api/v1/hr/org/authority/requests?status=PENDING')
      .then((r) => { if (vivant) setRows(shapeAuthorityRequests(r, moi)) })
      .catch(() => { if (vivant) setRows(null) })
    return () => { vivant = false }
  }, [version, moi.id, moi.displayName])

  const decider = async () => {
    if (!decision || busy) return
    setBusy(true)
    const body: Record<string, unknown> = { decision: decision.decision }
    if (decision.note.trim()) body['note'] = decision.note.trim()
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/hr/org/authority/requests/${decision.row.id}/decide`, body)
    setBusy(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Décision refusée.'); return }
    toast('ok', decision.decision === 'APPROVED' ? 'Demande approuvée — le changement est appliqué.' : 'Demande refusée.')
    setDecision(null)
    setVersion((v) => v + 1)
  }

  return (
    <section className="lc-card org-section" aria-labelledby="org-authority-queue">
      <div className="org-section__head">
        <h2 id="org-authority-queue"><ShieldAlert size={17} aria-hidden="true" /> Demandes d’autorité en attente</h2>
      </div>
      <p className="lc-hint" style={{ margin: 0 }}>
        Un responsable qui élargit son propre périmètre ne s’auto-approuve pas :
        sa demande attend ici la décision d’un tiers habilité.
      </p>
      {rows === 'loading' ? <p className="org-empty">Chargement…</p>
        : rows === null ? <p className="org-empty">File indisponible — le serveur n’a pas répondu.</p>
        : rows.length === 0 ? <p className="org-empty">Aucune demande en attente.</p>
        : rows.map((r) => (
          <div className="rh-assign" key={r.id}>
            <span className="lc-stack">
              <span><strong>{AUTHORITY_ACTION_LABEL[r.actionKind ?? ''] ?? 'Changement d’autorité'}</strong>{r.subject ? ` · ${r.subject}` : ''}</span>
              <span className="rh-assign__meta">
                Demandé par {r.actorName ?? 'un responsable'}
                {r.proposedAt ? ` · ${new Date(r.proposedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })}` : ''}
                {r.before !== null && r.after !== null
                  ? ` · impact figé : ${gens(r.before)} → ${gens(r.after)}${r.delta !== null ? ` (${r.delta > 0 ? '+' : ''}${r.delta})` : ''}`
                  : r.delta !== null ? ` · impact figé : ${r.delta > 0 ? '+' : ''}${r.delta}` : ''}
              </span>
              {r.mine ? <span className="rh-assign__meta">Votre demande — un autre responsable habilité doit décider.</span> : null}
            </span>
            {!r.mine ? (
              <span className="org-actions">
                <button className="lc-btn lc-btn--ghost" onClick={() => setDecision({ row: r, decision: 'REJECTED', note: '' })}>Refuser</button>
                <button className="lc-btn lc-btn--primary" style={{ marginLeft: 'var(--sp-2)' }} onClick={() => setDecision({ row: r, decision: 'APPROVED', note: '' })}>Approuver</button>
              </span>
            ) : null}
          </div>
        ))}

      <Modal open={decision !== null} onClose={() => setDecision(null)} ariaLabel="Décision sur une demande d’autorité" width={520}>
        {decision ? (
          <div className="org-editor">
            <h3>{decision.decision === 'APPROVED' ? 'Approuver la demande ?' : 'Refuser la demande ?'}</h3>
            <p className="lc-hint" style={{ margin: 0 }}>
              {AUTHORITY_ACTION_LABEL[decision.row.actionKind ?? ''] ?? 'Changement d’autorité'}
              {decision.row.subject ? ` · ${decision.row.subject}` : ''} — demandé par {decision.row.actorName ?? 'un responsable'}.
              {decision.decision === 'APPROVED' ? ' L’approbation applique le changement immédiatement.' : ''}
            </p>
            <Field label={decision.decision === 'APPROVED' ? 'Note (optionnel)' : 'Motif du refus'}>
              <TextInput value={decision.note} onChange={(v) => setDecision({ ...decision, note: v })} autoFocus />
            </Field>
            <div className="org-editor__foot">
              <button className="lc-btn lc-btn--ghost" onClick={() => setDecision(null)}>Annuler</button>
              <button className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void decider()}>
                {busy ? 'Envoi…' : decision.decision === 'APPROVED' ? 'Approuver' : 'Refuser'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  )
}

/* ═══════════ ÉQUIPES — liste ═══════════ */

export function HrTeamsPage() {
  const teams = useApi<{ teams: TeamRow[] }>('/api/v1/hr/teams')
  const perms = usePermissions()
  const toast = useToast()
  const canEdit = !perms.loading && perms.permissions.includes('hr.org.edit')
  const canAuthority = !perms.loading && perms.permissions.includes(AUTHORITY_PERM)
  const [editor, setEditor] = useState<{ id?: string; name: string; description: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!editor || saving) return
    setSaving(true)
    const body: Record<string, unknown> = { name: editor.name.trim() }
    if (editor.id) body['id'] = editor.id
    if (editor.description.trim()) body['description'] = editor.description.trim()
    const r = await adminMutate<{ error?: string; message?: string; id?: string }>('POST', '/api/v1/hr/teams', body)
    setSaving(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `Équipe ${editor.id ? 'mise à jour' : 'créée'}.`)
    setEditor(null)
    teams.reload()
  }

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead org-pagehead--row">
        <div>
          <h1 className="lc-title" style={{ margin: 0 }}>Équipes</h1>
          <p className="lc-hint">Les équipes du club — membres, responsables, historique. Rien n’est figé : tout est administrable.</p>
        </div>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => setEditor({ name: '', description: '' })}><Plus size={15} /> Nouvelle équipe</button> : null}
      </header>

      {teams.data && 'teams' in teams.data ? (
        teams.data.teams.length === 0 ? (
          <section className="lc-panel gd-panel"><div className="lc-panel__body">
            <p className="lc-hint" style={{ margin: 0 }}>Aucune équipe pour l’instant. Créez la première — Direction, Bar, Technique… à votre main.</p>
          </div></section>
        ) : (
          <div className="org-teamlist">
            {teams.data.teams.map((t) => (
              <Link key={t.id} className={`lc-card org-team${t.active ? '' : ' org-row--inactive'}`} href={`/equipe/organisation/equipes/${t.id}`}>
                <div className="org-team__head">
                  <span className="org-team__icon"><Users size={18} aria-hidden="true" /></span>
                  <span className="lc-stack">
                    <strong>{t.name}</strong>
                    {t.description ? <span className="org-desc">{t.description}</span> : null}
                  </span>
                </div>
                <div className="org-team__facts">
                  <span>{t.members} membre{t.members > 1 ? 's' : ''}</span>
                  {t.leads.length > 0 ? <span><Crown size={12} aria-hidden="true" /> {t.leads.join(' · ')}</span> : null}
                  {t.location ? <span><MapPin size={12} aria-hidden="true" /> {t.location.name}</span> : null}
                </div>
              </Link>
            ))}
          </div>
        )
      ) : <p className="lc-hint">Chargement…</p>}

      {/* La file des demandes d'autorité n'a de sens que pour qui peut décider. */}
      {canAuthority ? <div className="org-grid" style={{ marginTop: 'var(--sp-4)' }}><AuthorityRequestsPanel /></div> : null}

      <Modal open={editor !== null} onClose={() => setEditor(null)} ariaLabel="Équipe" width={460}>
        {editor ? (
          <div className="org-editor">
            <h3>{editor.id ? 'Modifier' : 'Créer'} une équipe</h3>
            <Field label="Nom"><TextInput value={editor.name} onChange={(v) => setEditor({ ...editor, name: v })} placeholder="Bar, Technique, Accueil…" autoFocus /></Field>
            <Field label="Description (optionnel)"><TextInput value={editor.description} onChange={(v) => setEditor({ ...editor, description: v })} /></Field>
            <div className="org-editor__foot">
              <button className="lc-btn lc-btn--ghost" onClick={() => setEditor(null)}>Annuler</button>
              <button className="lc-btn lc-btn--primary" disabled={saving || !editor.name.trim()} onClick={() => void save()}>Enregistrer</button>
            </div>
          </div>
        ) : null}
      </Modal>
    </main>
  )
}

/* ═══════════ ÉQUIPE — détail ═══════════ */

interface TeamDetail {
  team: { id: string; name: string; description: string | null; active: boolean; location: { id: string; name: string } | null }
  members: { assignment_id: string; person_id: string; display_name: string; is_lead: boolean; effective_from: string; position: string | null; functions: string[] }[]
  history: { display_name: string; is_lead: boolean; effective_from: string; effective_until: string }[]
}

export function HrTeamDetailPage({ id }: { id: string }) {
  const d = useApi<TeamDetail>(`/api/v1/hr/teams/${id}`)
  const perms = usePermissions()
  const toast = useToast()
  const confirm = useConfirm()
  const canEdit = !perms.loading && perms.permissions.includes('hr.org.edit')
  const canAuthority = !perms.loading && perms.permissions.includes(AUTHORITY_PERM)
  const menu = useMenuPersonne()
  const [people, setPeople] = useState<{ id: string; display_name: string }[]>([])
  const [add, setAdd] = useState({ person: '', lead: false })
  /** Action d'autorité en attente de confirmation (aperçu d'impact ouvert). */
  const [authority, setAuthority] = useState<{
    action: AuthorityAction; subjectName: string; title: string; confirmLabel: string; consequence?: string
    request: { method: string; path: string; body: Record<string, unknown> }
  } | null>(null)

  useEffect(() => {
    if (!canEdit) return
    apiGet<PeopleList>('/api/v1/rh/people').then((r) => { if ('people' in r) setPeople(r.people) }).catch(() => undefined)
  }, [canEdit])

  const today = () => new Date().toISOString().slice(0, 10)

  const addMember = async () => {
    if (!add.person) return
    const jour = today()
    // Responsable d'équipe = acte d'AUTORITÉ : aperçu d'impact d'abord (§18).
    if (add.lead) {
      const nom = people.find((p) => p.id === add.person)?.display_name ?? 'cette personne'
      setAuthority({
        action: { kind: 'TEAM_LEAD_ADD', team_id: id, person_id: add.person, effective_from: jour },
        subjectName: nom,
        title: AUTHORITY_ACTION_LABEL['TEAM_LEAD_ADD']!,
        confirmLabel: 'Désigner responsable',
        request: { method: 'POST', path: `/api/v1/hr/teams/${id}/members`, body: { person_id: add.person, is_lead: true, effective_from: jour } },
      })
      return
    }
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/teams/${id}/members`,
      { person_id: add.person, is_lead: false, effective_from: jour })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Membre ajouté.')
    setAdd({ person: '', lead: false })
    d.reload()
  }
  const endMember = async (assignmentId: string, name: string, isLead: boolean) => {
    const jour = today()
    // Retirer un RESPONSABLE réduit un périmètre : acte d'autorité lui aussi.
    if (isLead) {
      setAuthority({
        action: { kind: 'TEAM_LEAD_END', assignment_id: assignmentId, effective_until: jour },
        subjectName: name,
        title: AUTHORITY_ACTION_LABEL['TEAM_LEAD_END']!,
        // LE BOUTON DIT L'EFFET RÉEL. Côté serveur, clore une responsabilité
        // clôt la LIGNE d'appartenance (`hr_team_member.effective_until`) : il
        // n'existe aucun chemin qui retire le seul drapeau `is_lead`. Promettre
        // « retirer la responsabilité » faisait disparaître la personne de
        // l'équipe sans l'avoir annoncé.
        confirmLabel: 'Sortir de l’équipe',
        consequence: `Sortie à effet d’aujourd’hui (${jour}). ${name} n’est plus membre de l’équipe ; l’historique est conservé.`,
        request: { method: 'POST', path: `/api/v1/hr/team-members/${assignmentId}/end`, body: { effective_until: jour } },
      })
      return
    }
    if (!(await confirm({ title: `Retirer ${name} de l'équipe ?`, message: `Sortie à effet d'aujourd'hui (${jour}). L'historique est conservé.`, variant: 'standard', confirmLabel: 'Retirer' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/team-members/${assignmentId}/end`, { effective_until: jour })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    d.reload()
  }

  if (!d.data || !('team' in d.data)) return <main className="lc-page"><p className="lc-hint">Chargement…</p></main>
  const t = d.data

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail leaf={t.team.name} />
      <SmartBack fallback="/equipe/organisation/equipes" />
      <header className="org-teamhero">
        <span className="org-team__icon org-team__icon--lg"><Users size={26} aria-hidden="true" /></span>
        <div className="lc-stack">
          <h1 className="lc-title" style={{ margin: 0 }}>{t.team.name}</h1>
          <div className="rh-personhead__meta">
            <span>{t.members.length} membre{t.members.length > 1 ? 's' : ''}</span>
            {t.team.location ? <span><MapPin size={12} aria-hidden="true" /> {t.team.location.name}</span> : null}
            {t.team.description ? <span>{t.team.description}</span> : null}
          </div>
        </div>
      </header>

      <div className="org-grid">
        <section className="lc-card org-section">
          <div className="org-section__head"><h2><Users size={17} /> Membres</h2></div>
          {t.members.length === 0 ? <p className="org-empty">Aucun membre actif.</p> : t.members.map((m) => (
            <div className="rh-assign" key={m.assignment_id}>
              <Link className="lc-stack org-memberlink" href={`/equipe/rh/salaries/${m.person_id}`} {...menu.personne({ id: m.person_id, nom: m.display_name })}>
                <span>{m.is_lead ? <Crown size={13} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} /> : null}<strong>{m.display_name}</strong></span>
                <span className="rh-assign__meta">
                  {m.position ?? 'Sans poste'}{m.functions.length > 0 ? ` · ${m.functions.join(', ')}` : ''} · depuis {m.effective_from}
                </span>
              </Link>
              {/* Retirer un responsable est un acte d'autorité : le bouton est
                  ABSENT sans le droit dédié, jamais grisé (DS §8). */}
              {(m.is_lead ? canAuthority : canEdit)
                ? <button className="lc-btn lc-btn--ghost" onClick={() => void endMember(m.assignment_id, m.display_name, m.is_lead)}>Retirer</button>
                : null}
            </div>
          ))}
          {canEdit ? (
            <div className="lc-stack" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <span className="lc-label">Ajouter un membre</span>
              <Combobox
                value={add.person || null}
                items={people.filter((p) => !t.members.some((m) => m.person_id === p.id)).map((p) => ({ value: p.id, label: p.display_name }))}
                onChange={(v) => setAdd({ ...add, person: v ?? '' })}
                placeholder="Choisir un salarié…" ariaLabel="Salarié à ajouter" clearable
              />
              {canAuthority ? (
                <>
                  <Checkbox checked={add.lead} onChange={(v) => setAdd({ ...add, lead: v })} label="Responsable d’équipe" />
                  <span className="org-authority__hint">{AUTHORITY_HINT}</span>
                </>
              ) : null}
              <div><button className="lc-btn" disabled={!add.person} onClick={() => void addMember()}>Ajouter</button></div>
            </div>
          ) : null}
        </section>

        <section className="lc-card org-section">
          <div className="org-section__head"><h2><Star size={17} /> Historique</h2></div>
          {t.history.length === 0 ? <p className="org-empty">Aucune sortie enregistrée — l’histoire de l’équipe s’écrira ici.</p> : t.history.map((h, i) => (
            <div className="rh-assign" key={i}>
              <span className="lc-stack">
                <span>{h.display_name}{h.is_lead ? ' (responsable)' : ''}</span>
                <span className="rh-assign__meta">{h.effective_from} → {h.effective_until}</span>
              </span>
            </div>
          ))}
        </section>
      </div>

      <AuthorityImpactModal
        open={authority !== null}
        action={authority?.action ?? null}
        subjectName={authority?.subjectName ?? ''}
        {...(authority?.consequence ? { consequence: authority.consequence } : {})}
        title={authority?.title ?? ''}
        confirmLabel={authority?.confirmLabel ?? 'Confirmer'}
        request={authority?.request ?? null}
        onClose={() => setAuthority(null)}
        onApplied={() => { setAdd({ person: '', lead: false }); d.reload() }}
      />
    </main>
  )
}

/* ═══════════ COMPÉTENCES ═══════════ */

interface SkillsData {
  levels: { code: string; label: string; rank: number }[]
  skills: { id: string; name: string; description: string | null; active: boolean; holders: { person_id: string; display_name: string; level_code: string }[] }[]
}

export function HrSkillsPage() {
  const d = useApi<SkillsData>('/api/v1/hr/skills')
  const perms = usePermissions()
  const toast = useToast()
  const canEdit = !perms.loading && perms.permissions.includes('hr.org.edit')
  const menu = useMenuPersonne()
  const [editor, setEditor] = useState<{ id?: string; name: string; description: string } | null>(null)

  const save = async () => {
    if (!editor) return
    const body: Record<string, unknown> = { name: editor.name.trim() }
    if (editor.id) body['id'] = editor.id
    if (editor.description.trim()) body['description'] = editor.description.trim()
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/skills', body)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Compétence enregistrée.')
    setEditor(null)
    d.reload()
  }

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead org-pagehead--row">
        <div>
          <h1 className="lc-title" style={{ margin: 0 }}>Compétences</h1>
          <p className="lc-hint">Le catalogue des compétences et qui les détient — niveaux {d.data && 'levels' in d.data ? d.data.levels.map((l) => l.label).join(' → ') : '…'}.</p>
        </div>
        {canEdit ? <button className="lc-btn lc-btn--primary" onClick={() => setEditor({ name: '', description: '' })}><Plus size={15} /> Nouvelle compétence</button> : null}
      </header>

      {d.data && 'skills' in d.data ? (
        d.data.skills.length === 0 ? (
          <section className="lc-panel gd-panel"><div className="lc-panel__body"><p className="lc-hint" style={{ margin: 0 }}>Aucune compétence au catalogue. Les niveaux s’attribuent depuis la fiche salarié (Développement).</p></div></section>
        ) : (
          <div className="org-grid">
            {d.data.skills.map((s) => (
              <section key={s.id} className="lc-card org-section">
                <div className="org-section__head">
                  <h2><Star size={17} /> {s.name}</h2>
                  {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => setEditor({ id: s.id, name: s.name, description: s.description ?? '' })}>Modifier</button> : null}
                </div>
                {s.description ? <p className="org-desc" style={{ marginTop: 0 }}>{s.description}</p> : null}
                {s.holders.length === 0 ? <p className="org-empty">Personne ne détient encore cette compétence.</p> : (
                  <div>
                    {s.holders.map((h) => (
                      <div className="rh-assign" key={h.person_id}>
                        <Link className="org-memberlink" href={`/equipe/rh/salaries/${h.person_id}`} {...menu.personne({ id: h.person_id, nom: h.display_name })}>{h.display_name}</Link>
                        <span className="lc-pill lc-pill--info">{d.data && 'levels' in d.data ? d.data.levels.find((l) => l.code === h.level_code)?.label ?? h.level_code : h.level_code}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )
      ) : <p className="lc-hint">Chargement…</p>}

      <Modal open={editor !== null} onClose={() => setEditor(null)} ariaLabel="Compétence" width={460}>
        {editor ? (
          <div className="org-editor">
            <h3>{editor.id ? 'Modifier' : 'Créer'} une compétence</h3>
            <Field label="Nom"><TextInput value={editor.name} onChange={(v) => setEditor({ ...editor, name: v })} autoFocus /></Field>
            <Field label="Description (optionnel)"><TextInput value={editor.description} onChange={(v) => setEditor({ ...editor, description: v })} /></Field>
            <div className="org-editor__foot">
              <button className="lc-btn lc-btn--ghost" onClick={() => setEditor(null)}>Annuler</button>
              <button className="lc-btn lc-btn--primary" disabled={!editor.name.trim()} onClick={() => void save()}>Enregistrer</button>
            </div>
          </div>
        ) : null}
      </Modal>
    </main>
  )
}

/* ═══════════ ORGANIGRAMME ═══════════ */

interface ChartData {
  people: { person_id: string; display_name: string; position: string | null; teams: string[]; state: string | null }[]
  relations: { person_id: string; manager_person_id: string; kind: string }[]
}

const REL_LABEL: Record<string, string> = { PRIMARY: 'principal', SECONDARY: 'secondaire', OPERATIONAL: 'opérationnel' }

/**
 * Organigramme SOUPLE (§19) : pas de pyramide 1998 — on groupe par
 * responsable, une personne peut apparaître sous plusieurs responsables
 * (relations secondaires/opérationnelles marquées), clic → fiche salarié.
 */
export function HrOrgChartPage() {
  const d = useApi<ChartData>('/api/v1/hr/orgchart-v2')
  const menu = useMenuPersonne()
  if (!d.data || !('people' in d.data)) return <main className="lc-page"><p className="lc-hint">Chargement…</p></main>
  const byId = new Map(d.data.people.map((p) => [p.person_id, p]))
  const managers = [...new Set(d.data.relations.map((r) => r.manager_person_id))]
    .map((id) => byId.get(id)).filter((p): p is NonNullable<typeof p> => !!p)
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
  const managed = new Set(d.data.relations.map((r) => r.person_id))
  const unattached = d.data.people.filter((p) => !managed.has(p.person_id) && !managers.some((m) => m.person_id === p.person_id))

  return (
    <main className="lc-page lc-page--management lc-stagger">
      <Trail />
      <SmartBack fallback="/equipe/organisation" />
      <header className="org-pagehead">
        <h1 className="lc-title" style={{ margin: 0 }}>Organigramme</h1>
        <p className="lc-hint">Qui répond à qui — responsables principaux, secondaires et opérationnels. Les rattachements se définissent depuis la fiche de chaque salarié.</p>
      </header>

      {managers.length === 0 ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          <p className="lc-hint" style={{ margin: 0 }}>Aucun rattachement défini pour l’instant. Ouvrez une fiche salarié → Emploi → « Responsable » pour construire l’organigramme.</p>
        </div></section>
      ) : (
        <div className="org-chart">
          {managers.map((m) => {
            const reports = d.data!.relations.filter((r) => r.manager_person_id === m.person_id)
            return (
              <section className="lc-card org-chartnode" key={m.person_id}>
                <button className="org-chartperson org-chartperson--manager" onClick={() => navigate(`/equipe/rh/salaries/${m.person_id}`)}
                  {...menu.personne({ id: m.person_id, nom: m.display_name })}>
                  <span className="org-avatar" aria-hidden="true">{initials(m.display_name)}</span>
                  <span className="lc-stack">
                    <strong>{m.display_name}</strong>
                    <span className="rh-assign__meta">{m.position ?? '—'}{m.teams.length ? ` · ${m.teams.join(', ')}` : ''}</span>
                  </span>
                </button>
                <div className="org-chartreports">
                  {reports.map((r) => {
                    const p = byId.get(r.person_id)
                    if (!p) return null
                    return (
                      <button className="org-chartperson" key={r.person_id + r.kind} onClick={() => navigate(`/equipe/rh/salaries/${p.person_id}`)}
                        {...menu.personne({ id: p.person_id, nom: p.display_name })}>
                        <span className="org-avatar" aria-hidden="true">{initials(p.display_name)}</span>
                        <span className="lc-stack">
                          <span>{p.display_name}</span>
                          <span className="rh-assign__meta">{p.position ?? '—'}{r.kind !== 'PRIMARY' ? ` · ${REL_LABEL[r.kind]}` : ''}</span>
                        </span>
                        {r.kind !== 'PRIMARY' ? <span className={`lc-pill ${r.kind === 'SECONDARY' ? 'lc-pill--info' : 'lc-pill--idle'}`}>{REL_LABEL[r.kind]}</span> : null}
                      </button>
                    )
                  })}
                </div>
              </section>
            )
          })}
          {unattached.length > 0 ? (
            <section className="lc-card org-chartnode org-chartnode--rest">
              <h2 className="lc-section-title" style={{ margin: 0 }}><Network size={15} aria-hidden="true" /> Sans rattachement</h2>
              <div className="org-chartreports">
                {unattached.map((p) => (
                  <button className="org-chartperson" key={p.person_id} onClick={() => navigate(`/equipe/rh/salaries/${p.person_id}`)}
                    {...menu.personne({ id: p.person_id, nom: p.display_name })}>
                    <span className="org-avatar" aria-hidden="true">{initials(p.display_name)}</span>
                    <span className="lc-stack"><span>{p.display_name}</span><span className="rh-assign__meta">{p.position ?? '—'}</span></span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
      <p className="lc-hint"><Building2 size={13} aria-hidden="true" /> Une personne peut avoir plusieurs responsables — l’organigramme n’impose aucune pyramide.</p>
    </main>
  )
}

function initials(name: string): string {
  return name.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()
}
